import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  KioskSessionFilters, 
  KioskSessionGridRow, 
  KioskSessionDetail,
  HierarchicalSessionRow 
} from '../types/kiosk-session-types';
import { servicesContainer } from '@/core/di/services-container';
import { useOperatorStore } from '@/core/stores/operator-store';

interface KioskSessionsStore {
  // Data state
  sessions: KioskSessionGridRow[];
  selectedSessionDetail: KioskSessionDetail | null;
  selectedSessionId: string | null;
  groupedSessions: Map<string, KioskSessionGridRow[]>;
  hierarchicalSessions: HierarchicalSessionRow[];
  filters: KioskSessionFilters;
  totalCount: number;

  // Pagination state (cursor-based for Kiosk API)
  currentPage: number;
  pageSize: number;
  pageSizeOptions: number[];
  hasMorePages: boolean;
  lastSessionStartTime: string | null;
  pageCursors: Map<number, string>; // Maps page number to the 'from' datetime cursor

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  isLoadingMore: boolean;

  // Error states
  error: string | null;

  // UI state
  groupByLocation: boolean;
  lastUpdated: Date | null;

  // Health monitoring
  systemHealth: {
    isHealthy: boolean;
    authenticationStatus: string;
    apiConnectivityStatus: string;
    errorMessage?: string;
  } | null;

  // Actions
  loadSessions: (filters: KioskSessionFilters) => Promise<void>;
  loadPage: (page: number) => Promise<void>;
  setPageSize: (size: number) => void;
  loadMoreSessions: () => Promise<void>;
  setSelectedSessionFromGrid: (session: KioskSessionGridRow) => void;
  loadGroupedSessions: (filters: KioskSessionFilters) => Promise<void>;
  setFilters: (filters: Partial<KioskSessionFilters>) => void;
  setSelectedSession: (sessionId: string | null) => void;
  refreshSessions: () => Promise<void>;
  toggleGroupByLocation: () => void;
  checkSystemHealth: () => Promise<void>;
  clearErrors: () => void;
  clearSelection: () => void;
  clearData: () => void;
}

// Default filters - last week from today
const getDefaultFilters = (): KioskSessionFilters => {
  const today = new Date();
  const weekAgo = new Date();
  weekAgo.setDate(today.getDate() - 7);
  
  return {
    dateFrom: weekAgo.toLocaleDateString('en-GB'), // DD.MM.YYYY format
    dateTo: today.toLocaleDateString('en-GB'),
    groupByLocation: false,
  };
};

export const useKioskSessionsStore = create<KioskSessionsStore>()(
  persist(
    (set, get) => ({
      // Initial state
      sessions: [],
      selectedSessionDetail: null,
      selectedSessionId: null,
      groupedSessions: new Map(),
      hierarchicalSessions: [],
      filters: getDefaultFilters(),
      totalCount: 0,
      pageCursors: new Map(),
      currentPage: 0,
      pageSize: 100,
      pageSizeOptions: [10, 25, 50, 100, 200, 500, 1000],
      hasMorePages: true,
      lastSessionStartTime: null,
      isLoading: false,
      isRefreshing: false,
      isLoadingMore: false,
      error: null,
      groupByLocation: false,
      lastUpdated: null,
      systemHealth: null,

      loadSessions: async (filters: KioskSessionFilters) => {
        const { pageSize } = get();
        set({
          isLoading: true,
          error: null,
          filters,
          sessions: [],
          lastSessionStartTime: null,
          hasMorePages: true,
          currentPage: 0,
          pageCursors: new Map() // Reset cursors when loading new data
        });

        try {
          // Get services lazily to avoid circular dependency
          const services = servicesContainer.getServices();

          // First page - use original date range, no skip parameter
          const filtersWithPagination: KioskSessionFilters = {
            ...filters,
            limit: pageSize,
            // Don't include skip - Kiosk API doesn't support it
          };

          const result = await services.getKioskSessionsUseCase.getSessionsForGrid(filtersWithPagination);

          if (result.hasError) {
            set({
              isLoading: false,
              error: result.errorMessage || 'Failed to load sessions',
              sessions: [],
              totalCount: 0,
            });
            return;
          }

          // Determine if there are more pages
          const hasMore = filters.limit ? result.data.length >= filters.limit : false;

          // Get last session start time for cursor-based pagination (YYYY-MM-DD format)
          const lastStartTime = result.data.length > 0
            ? result.data[result.data.length - 1].start_time.toISOString()
            : null;


          // Store cursor for page 1 (next page will use this as 'to' datetime)
          const newCursors = new Map<number, string>();
          if (lastStartTime) {
            newCursors.set(1, lastStartTime); // Page 1's cursor is the last session's start time
          }

          set({
            sessions: result.data,
            totalCount: result.totalCount,
            isLoading: false,
            lastUpdated: new Date(),
            error: null,
            hasMorePages: hasMore,
            lastSessionStartTime: lastStartTime,
            pageCursors: newCursors,
            currentPage: 0,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load sessions',
            sessions: [],
            totalCount: 0,
          });
        }
      },

      /**
       * Load a specific page using cursor-based pagination
       * For Kiosk API, each page uses the last session's datetime as the new 'from' parameter
       */
      loadPage: async (page: number) => {
        const { filters, pageSize, totalCount } = get();

        // Page 0 - reload from original date range (first page, no skip)
        if (page === 0) {
          await get().loadSessions(filters);
          return;
        }

        set({ currentPage: page, isRefreshing: true, error: null });

        try {
          const services = servicesContainer.getServices();

          // Skip-based pagination: jump to any page directly using original from/to range
          const filtersWithSkip: KioskSessionFilters = {
            ...filters,
            skip: page * pageSize,
            limit: pageSize,
          };

          console.log(`📄 Loading page ${page} with skip=${page * pageSize}, limit=${pageSize}`);

          const result = await services.getKioskSessionsUseCase.getSessionsForGrid(filtersWithSkip);

          if (result.hasError) {
            set({
              isRefreshing: false,
              error: result.errorMessage || 'Failed to load page',
              sessions: [],
            });
            return;
          }

          set({
            sessions: result.data,
            totalCount: totalCount, // Keep original total count
            isRefreshing: false,
            lastUpdated: new Date(),
            error: null,
            hasMorePages: result.data.length >= pageSize,
          });
        } catch (error) {
          set({
            isRefreshing: false,
            error: error instanceof Error ? error.message : 'Failed to load page',
          });
        }
      },

      /**
       * Change page size and reset to first page
       * Clears cursor cache since page size affects pagination
       */
      setPageSize: (size: number) => {
        const { filters } = get();
        set({
          pageSize: size,
          currentPage: 0,
          pageCursors: new Map(), // Clear cursors when page size changes
        });

        // Reload sessions with new page size
        get().loadSessions(filters);
      },

      loadMoreSessions: async () => {
        const state = get();
        
        if (!state.hasMorePages || state.isLoadingMore || !state.lastSessionStartTime) {
          return;
        }
        
        set({ isLoadingMore: true, error: null });
        
        try {
          // Create filters for next page using last session's start time as new upper bound (descending)
          const nextPageFilters: KioskSessionFilters = {
            ...state.filters,
            dateTo: state.lastSessionStartTime,
          };
          
          // Get services lazily to avoid circular dependency
          const services = servicesContainer.getServices();
          const result = await services.getKioskSessionsUseCase.getSessionsForGrid(nextPageFilters);
          
          if (result.hasError) {
            set({
              isLoadingMore: false,
              error: result.errorMessage || 'Failed to load more sessions',
            });
            return;
          }
          
          // Determine if there are more pages
          const hasMore = state.filters.limit ? result.data.length >= state.filters.limit : false;
          
          // Get last session start time for next pagination (full ISO timestamp for precision)
          const lastStartTime = result.data.length > 0
            ? result.data[result.data.length - 1].start_time.toISOString()
            : null;
          
          set({
            sessions: [...state.sessions, ...result.data],
            totalCount: state.totalCount + result.data.length,
            isLoadingMore: false,
            lastUpdated: new Date(),
            error: null,
            hasMorePages: hasMore,
            lastSessionStartTime: lastStartTime,
          });
        } catch (error) {
          set({
            isLoadingMore: false,
            error: error instanceof Error ? error.message : 'Failed to load more sessions',
          });
        }
      },

      setSelectedSessionFromGrid: (session: KioskSessionGridRow) => {
        // Convert grid row to detail format
        // Grid row has Date objects, but detail expects string fields for proper display
        const detail: KioskSessionDetail = {
          ...session,
          // Convert Date objects to formatted ISO strings for display
          start_time: session.start_time instanceof Date
            ? session.start_time.toISOString()
            : String(session.start_time),
          stop_time: session.stop_time instanceof Date
            ? session.stop_time.toISOString()
            : String(session.stop_time),
          auth_time: session.authTime instanceof Date
            ? session.authTime.toISOString()
            : session.authTime,
          settlement_time: session.settTime instanceof Date
            ? session.settTime.toISOString()
            : session.settTime,
        };

        set({
          selectedSessionId: session.session_id,
          selectedSessionDetail: detail,
        });
      },

      loadGroupedSessions: async (filters: KioskSessionFilters) => {
        const groupedFilters = { ...filters, groupByLocation: true };
        set({ isLoading: true, error: null, filters: groupedFilters });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getKioskSessionsUseCase.getSessionsGroupedByLocation(groupedFilters);

          if (result.hasError) {
            set({
              isLoading: false,
              error: result.errorMessage || 'Failed to load grouped sessions',
              groupedSessions: new Map(),
              hierarchicalSessions: [],
              totalCount: 0,
            });
            return;
          }

          set({
            groupedSessions: result.groupedData,
            hierarchicalSessions: result.hierarchicalData,
            totalCount: result.totalCount,
            isLoading: false,
            lastUpdated: new Date(),
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load grouped sessions',
            groupedSessions: new Map(),
            hierarchicalSessions: [],
            totalCount: 0,
          });
        }
      },

      setFilters: (newFilters: Partial<KioskSessionFilters>) => {
        const currentFilters = get().filters;
        set({
          filters: { ...currentFilters, ...newFilters },
          currentPage: 0,  // Reset pagination when filters change
          pageCursors: new Map(),  // Clear cursor cache when filters change
        });
      },

      setSelectedSession: (sessionId: string | null) => {
        set({ 
          selectedSessionId: sessionId,
          selectedSessionDetail: null,
          detailError: null,
        });
      },

      refreshSessions: async () => {
        const state = get();
        set({ isRefreshing: true });

        try {
          if (state.groupByLocation) {
            await get().loadGroupedSessions(state.filters);
          } else {
            await get().loadSessions(state.filters);
          }
        } finally {
          set({ isRefreshing: false });
        }
      },

      toggleGroupByLocation: () => {
        const state = get();
        const newGroupByLocation = !state.groupByLocation;

        // Clear locationId when enabling grouping (mutual exclusion)
        const updatedFilters = {
          ...state.filters,
          groupByLocation: newGroupByLocation,
          locationId: newGroupByLocation ? undefined : state.filters.locationId
        };

        set({
          groupByLocation: newGroupByLocation,
          filters: updatedFilters,
        });

        // Reload data with new grouping preference
        if (newGroupByLocation) {
          get().loadGroupedSessions(updatedFilters);
        } else {
          get().loadSessions(updatedFilters);
        }
      },

      checkSystemHealth: async () => {
        try {
          // Get services lazily to avoid circular dependency
          const services = servicesContainer.getServices();
          const healthResult = await services.getKioskSessionsUseCase.testSystemHealth();
          
          set((state) => ({
            ...state,
            systemHealth: {
              isHealthy: healthResult.isHealthy,
              authenticationStatus: healthResult.details.authentication ? 'OK' : 'ERROR',
              apiConnectivityStatus: healthResult.details.apiConnectivity ? 'OK' : 'ERROR',
              errorMessage: healthResult.errorMessage,
            },
          }));
        } catch (error) {
          set((state) => ({
            ...state,
            systemHealth: {
              isHealthy: false,
              authenticationStatus: 'ERROR',
              apiConnectivityStatus: 'ERROR',
              errorMessage: error instanceof Error ? error.message : 'Health check failed',
            },
          }));
        }
      },

      clearErrors: () => set({ error: null }),

      clearSelection: () => set({
        selectedSessionId: null,
        selectedSessionDetail: null,
      }),

      clearData: () => set({
        sessions: [],
        selectedSessionDetail: null,
        selectedSessionId: null,
        groupedSessions: new Map(),
        hierarchicalSessions: [],
        totalCount: 0,
        error: null,
        lastUpdated: null,
        systemHealth: null,
      }),
    }),
    {
      name: 'kiosk-sessions-storage',
      partialize: (state) => ({
        // Persist user preferences and filters
        // The selected sub-operator is session-only: it depends on the operator and is re-validated per load
        filters: { ...state.filters, subOperatorId: undefined },
        groupByLocation: state.groupByLocation,
        // Don't persist data, loading states, or errors
      }),
      // Drop a sub-operator saved by earlier versions so it is never restored on reopen
      merge: (persisted, current) => {
        const saved = persisted as Partial<KioskSessionsStore> | undefined;
        return {
          ...current,
          ...saved,
          filters: { ...current.filters, ...saved?.filters, subOperatorId: undefined },
        };
      },
    }
  )
);